import { useState, useEffect, useMemo, useRef } from "react";
import SolidGaugeComponent from "./SolidGaugeComponent";
import StandardChartComponent from "./StandardChartComponent";
import BooleanIndicatorComponent from "./BooleanIndicatorComponent";
import BooleanIndicatorProperties from "./BooleanIndicatorProperties";
import TemperatureGaugeComponent from "./TemperatureGaugeComponent";
import TemperatureGaugeProperties from "./TemperatureGaugeProperties";
import WindroseChartCard from "./WindroseChartCard";
import { Dropdown, Modal, Button, Row, Col } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import GaugePropertiesModal from "./GaugePropertiesModal";
import ChartPropertiesModal from "./ChartPropertiesModal";
import WindrosePropertiesModal from "./WindrosePropertiesModal";
import { useAuth } from "context/AuthContext";
import { Maximize2 } from "lucide-react";
import { DateTime } from "luxon";
import { useTimer } from "context/TimerContext";
import http from "services/httpClient";

interface ChartPoint {
  timestamp: string;
  values: Record<string, number | null>;
}

interface StatDefinition {
  id: number;
  result_label: string;
  source_column_index: number;
  aggregation: "min" | "max" | "avg";
  window_hours: 1 | 6 | 8 | 12 | 24;
  unit?: string;
}


export function DashboardCard({
  config,
  onDelete,
  onDuplicate,
  clientRole,
}: {
  config: any;
  onDelete: () => void;
  onDuplicate: (config: any) => void;
  clientRole: string | null;
}) {
  const [hovered, setHovered] = useState(false);
  const [showProperties, setShowProperties] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState("1d");
  const [customRange, setCustomRange] = useState<[Date | null, Date | null]>([null, null]);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [chartData, setChartData] = useState<ChartPoint[]>([]);

  const [zoomData, setZoomData] = useState<ChartPoint[] | null>(null);
  const zoomRequestRef = useRef(0);

  const { user } = useAuth();
  const role = user?.role;
  const [showFullscreen, setShowFullscreen] = useState(false);
  const timezone = user?.timezone || "UTC";
  const [deviceLabels, setDeviceLabels] = useState<{ column_index: number; label: string }[]>([]);
  const lastTimestampRef = useRef<string | undefined>(undefined);
  const isChartType = config.type === "standard_chart" || config.type === "windrose_chart";
  const now = useTimer();

  const formatStatTimestamp = (ts: number) =>
    DateTime.fromMillis(ts, { zone: "utc" }).toFormat("yyyy-MM-dd HH:mm:ss");

  const parseAnyTimestamp = (ts: string) => {
    const fmt = DateTime.fromFormat(ts, "yyyy-MM-dd HH:mm:ss", { zone: "utc" });
    if (fmt.isValid) return fmt;

    const iso = DateTime.fromISO(ts, { zone: "utc" });
    if (iso.isValid) return iso;

    return DateTime.invalid("bad timestamp");
  };

  const parseChartTimestamp = (ts: string) => parseAnyTimestamp(ts).toMillis();

  const isStatColumn = (columnIndex: number) => columnIndex >= 1000;


  const normalizeChartRows = (rows: any[], rawColumns: number[]): ChartPoint[] => {
    return (rows || []).map((row) => {
      const tsText = parseAnyTimestamp(row.timestamp).toFormat("yyyy-MM-dd HH:mm:ss");

      const values: Record<string, number | null> = Object.fromEntries(
        rawColumns.map((col) => {
          const rawVal =
            row.values?.[String(col)] ??
            row.values?.[col] ??
            row.data_values?.[col] ??
            null;

          return [String(col), rawVal];
        })
      );

      return {
        timestamp: tsText,
        values,
      };
    });
  };

  const getSourceColumnScale = (sourceColumnIndex: number): { multiplier: number; offset: number } => {
    // Check config.chart_labels first (already resolved with multiplier/offset)
    if (Array.isArray(config.chart_labels)) {
      const found = config.chart_labels.find(
        (item: any) => (typeof item === "object" ? item.column_index : item) === sourceColumnIndex
      ) as any;
      if (found?.multiplier !== undefined) {
        return { multiplier: found.multiplier ?? 1, offset: found.offset ?? 0 };
      }
    }
    // Fall back to deviceLabels (contains full label objects from /device/:id/labels)
    const fallback = (deviceLabels as any[]).find((l) => l.column_index === sourceColumnIndex);
    return { multiplier: fallback?.multiplier ?? 1, offset: fallback?.offset ?? 0 };
  };

  const buildWindowStatRows = (
    sourcePoints: ChartPoint[],
    sourceColumnIndex: number,
    aggregation: "min" | "max" | "avg",
    windowHours: 1 | 6 | 8 | 12 | 24,
    syntheticColumnIndex: number,
    rangeEndTs: number
  ): ChartPoint[] => {
    const windowMs = windowHours * 60 * 60 * 1000;
    const rangeStartTs = rangeEndTs - 24 * 60 * 60 * 1000;
    const { multiplier: srcMul, offset: srcOff } = getSourceColumnScale(sourceColumnIndex);

    const validPoints = sourcePoints
      .map((p) => {
        const ts = parseChartTimestamp(p.timestamp);
        const raw = p.values?.[String(sourceColumnIndex)];
        const value = raw !== null && raw !== undefined ? raw * srcMul + srcOff : raw;
        return { ts, value };
      })
      .filter(
        (p) =>
          Number.isFinite(p.ts) &&
          p.value !== null &&
          p.value !== undefined &&
          Number.isFinite(p.value)
      ) as { ts: number; value: number }[];

    if (validPoints.length === 0) return [];

    validPoints.sort((a, b) => a.ts - b.ts);

    const result: ChartPoint[] = [];

    for (
      let windowStartTs = rangeStartTs;
      windowStartTs < rangeEndTs;
      windowStartTs += windowMs
    ) {
      const windowEndTs = Math.min(windowStartTs + windowMs, rangeEndTs);

      const bucketPoints = validPoints.filter((p) => {
        if (windowStartTs === rangeStartTs) {
          return p.ts >= windowStartTs && p.ts <= windowEndTs;
        }
        return p.ts > windowStartTs && p.ts <= windowEndTs;
      });

      if (bucketPoints.length === 0) continue;

      const windowVals = bucketPoints.map((p) => p.value);

      let value: number;
      let dotTs: number;

      if (aggregation === "avg") {
        value = windowVals.reduce((a, b) => a + b, 0) / windowVals.length;
        dotTs = Math.round((bucketPoints[0].ts + bucketPoints[bucketPoints.length - 1].ts) / 2);
      } else if (aggregation === "min") {
        value = Math.min(...windowVals);
        dotTs = bucketPoints.reduce((a, b) => (b.value < a.value ? b : a)).ts;
      } else {
        value = Math.max(...windowVals);
        dotTs = bucketPoints.reduce((a, b) => (b.value > a.value ? b : a)).ts;
      }

      result.push({
        timestamp: formatStatTimestamp(dotTs),
        values: { [String(syntheticColumnIndex)]: value },
      });
    }

    return result;
  };

  const mergeRowsByTimestamp = (baseRows: ChartPoint[], extraRows: ChartPoint[]): ChartPoint[] => {
    const map = new Map<string, ChartPoint>();

    for (const row of baseRows) {
      map.set(row.timestamp, {
        timestamp: row.timestamp,
        values: { ...row.values },
      });
    }

    for (const row of extraRows) {
      const existing = map.get(row.timestamp);
      if (existing) {
        map.set(row.timestamp, {
          ...existing,
          values: {
            ...existing.values,
            ...row.values,
          },
        });
      } else {
        map.set(row.timestamp, {
          timestamp: row.timestamp,
          values: { ...row.values },
        });
      }
    }

    return Array.from(map.values()).sort(
      (a, b) => parseChartTimestamp(a.timestamp) - parseChartTimestamp(b.timestamp)
    );
  };

  const removeStatColumnsFromRows = (rows: ChartPoint[]): ChartPoint[] => {
    return rows
      .map((row) => {
        const cleanedValues = Object.fromEntries(
          Object.entries(row.values || {}).filter(([key]) => !isStatColumn(Number(key)))
        );

        return {
          timestamp: row.timestamp,
          values: cleanedValues,
        };
      })
      .filter((row) => Object.keys(row.values).length > 0);
  };

  const injectStatRowsFor1D = (
    rawRows: ChartPoint[],
    statDefs: StatDefinition[],
    statChartColumns: number[],
    rangeEndTs: number
  ): ChartPoint[] => {
    let mergedRows = [...rawRows];

    const usedStatDefs = statDefs.filter((s) => statChartColumns.includes(1000 + s.id));

    for (const stat of usedStatDefs) {
      const syntheticColumnIndex = 1000 + stat.id;

      const statRows = buildWindowStatRows(
        rawRows,
        stat.source_column_index,
        stat.aggregation,
        stat.window_hours,
        syntheticColumnIndex,
        rangeEndTs
      );

      mergedRows = mergeRowsByTimestamp(mergedRows, statRows);
    }

    return mergedRows;
  };

  const handleResetZoom = () => {
    zoomRequestRef.current += 1;
    setZoomData(null);
  };

  const handleZoomRange = async (start: string, end: string) => {
    const requestId = ++zoomRequestRef.current;

    try {
      const deviceId = config.device_id;

      const rawColumns = derivedColumns.filter(
        (col: number) => !isStatColumn(col)
      );

      const statChartColumns =
        selectedRange === "1d"
          ? derivedColumns.filter((col: number) => isStatColumn(col))
          : [];

      let statDefs: StatDefinition[] = [];
      let sourceColumns: number[] = [];

      // Follow the same column-selection logic as the existing date-range fetch.
      if (statChartColumns.length > 0) {
        try {
          const statRes = await http.get(
            `/stat-definitions/device/${deviceId}`
          );

          statDefs = (statRes.data || []) as StatDefinition[];

          const usedStatDefs = statDefs.filter((s) =>
            statChartColumns.includes(1000 + s.id)
          );

          sourceColumns = usedStatDefs
            .map((s) => s.source_column_index)
            .filter((col) => !rawColumns.includes(col));
        } catch {
          // Continue without additional statistic source columns.
        }
      }

      const allRawColumns = [...rawColumns, ...sourceColumns];

      if (allRawColumns.length === 0) return;

      const params = new URLSearchParams();

      allRawColumns.forEach((col: number) => {
        params.append("columns", col.toString());
      });

      // Use the same timestamp format and endpoint as custom date ranges.
      params.append("start", start);
      params.append("end", end);
      params.append("tz", timezone);
      params.append("downsample", "true");
      params.append("target_points", "2000");

      const res = await http.get(
        `/data/${deviceId}/chart_data`,
        { params }
      );

      let rows = normalizeChartRows(res.data || [], allRawColumns);

      // Keep the existing 1-day statistic points where they fall inside
      // the selected zoom interval.
      // if (statChartColumns.length > 0 && statDefs.length > 0) {
      if (statChartColumns.length > 0) {
        const startTs = parseChartTimestamp(start);
        const endTs = parseChartTimestamp(end);

        const statRows = chartData
          .filter((row) => {
            const ts = parseChartTimestamp(row.timestamp);

            return (
              ts >= startTs &&
              ts <= endTs &&
              Object.keys(row.values || {}).some((key) =>
                isStatColumn(Number(key))
              )
            );
          })
          .map((row) => ({
            timestamp: row.timestamp,
            values: Object.fromEntries(
              Object.entries(row.values || {}).filter(([key]) =>
                isStatColumn(Number(key))
              )
            ) as Record<string, number | null>,
          }));

        rows = mergeRowsByTimestamp(rows, statRows);
      }

      // Ignore an older response if a newer zoom request or reset occurred.
      if (requestId === zoomRequestRef.current) {
        setZoomData(rows);
      }
    } catch {
      // Keep the current chart data if the zoom request fails.
    }
  };

  const derivedColumns = useMemo(() => {
    const base = Array.isArray(config.chart_labels)
      ? config.chart_labels.map((index: number | { column_index: number }) =>
          typeof index === "number" ? index : index.column_index
        )
      : [];

    if (selectedRange !== "1d") {
      return base.filter((col: number) => !isStatColumn(col));
    }

    return base;
  }, [config.chart_labels, selectedRange]);

  const displayConfig = useMemo(() => {
    if (!Array.isArray(config.chart_labels)) return config;

    const filteredChartLabels =
      selectedRange === "1d"
        ? config.chart_labels
        : config.chart_labels.filter((item: number | { column_index: number }) => {
            const col = typeof item === "number" ? item : item.column_index;
            return !isStatColumn(col);
          });

    return {
      ...config,
      chart_labels: filteredChartLabels,
    };
  }, [config, selectedRange]);

  useEffect(() => {
    zoomRequestRef.current += 1;
    setZoomData(null);

    const fetchData = async () => {
      if (selectedRange === "custom" && (!customRange[0] || !customRange[1])) return;

      if (!Array.isArray(derivedColumns) || derivedColumns.length === 0) {
        setChartData([]);
        return;
      }

      try {
        const deviceId = config.device_id;
        const rawColumns = derivedColumns.filter((col: number) => !isStatColumn(col));
        const statChartColumns = selectedRange === "1d"
          ? derivedColumns.filter((col: number) => isStatColumn(col))
          : [];

        // Fetch stat definitions first so we can include source columns in the data request.
        // buildWindowStatRows needs source column values (e.g. col 14) to compute stats correctly.
        let statDefs: StatDefinition[] = [];
        let sourceColumns: number[] = [];

        if (statChartColumns.length > 0) {
          try {
            const statRes = await http.get(`/stat-definitions/device/${deviceId}`);
            statDefs = (statRes.data || []) as StatDefinition[];
            const usedStatDefs = statDefs.filter((s) => statChartColumns.includes(1000 + s.id));
            sourceColumns = usedStatDefs
              .map((s) => s.source_column_index)
              .filter((col) => !rawColumns.includes(col));
          } catch {}
        }

        const allRawColumns = [...rawColumns, ...sourceColumns];
        const params = new URLSearchParams();

        allRawColumns.forEach((col: number) => {
          params.append("columns", col.toString());
        });

        if (selectedRange === "custom") {
          // params.append("start", customRange[0]!.toISOString());
          // params.append("end", customRange[1]!.toISOString());
          params.append(
              "start",
              DateTime.fromJSDate(customRange[0]!)
                  .toFormat("yyyy-MM-dd HH:mm:ss")
          );

          params.append(
              "end",
              DateTime.fromJSDate(customRange[1]!)
                  .toFormat("yyyy-MM-dd HH:mm:ss")
          );
        } else {
          params.append("range", selectedRange);
        }

        params.append("tz", timezone);
        params.append("downsample", "true");
        params.append("target_points", "2000");

        const res = await http.get(`/data/${deviceId}/chart_data`, { params });
        let rows = normalizeChartRows(res.data || [], allRawColumns);

        if (statDefs.length > 0 && statChartColumns.length > 0) {
          const lastRow = rows[rows.length - 1];
          const rangeEndTs = lastRow
            ? parseChartTimestamp(lastRow.timestamp)
            : DateTime.now().setZone(timezone).toMillis();
          rows = injectStatRowsFor1D(rows, statDefs, statChartColumns, rangeEndTs);
        }

        setChartData(rows);
      } catch {
        // ignore
      }
    };

    fetchData();
  }, [config.device_id, derivedColumns, selectedRange, customRange, timezone]);

  useEffect(() => {
    if (!isChartType || selectedRange !== "1d") return;
    if (!config.device_id || derivedColumns.length === 0) return;
    if (!now) return;

    const fetchLatest = async () => {
      try {
        const rawColumns = derivedColumns.filter((col: number) => !isStatColumn(col));
        const statChartColumns = derivedColumns.filter((col: number) => isStatColumn(col));

        // Fetch stat definitions first so source columns are included in the data request
        let statDefsForLatest: StatDefinition[] = [];
        let sourceColumns: number[] = [];

        if (statChartColumns.length > 0) {
          try {
            const statRes = await http.get(`/stat-definitions/device/${config.device_id}`);
            statDefsForLatest = statRes.data || [];
            const usedStatDefs = statDefsForLatest.filter((s) => statChartColumns.includes(1000 + s.id));
            sourceColumns = usedStatDefs
              .map((s) => s.source_column_index)
              .filter((col) => !rawColumns.includes(col));
          } catch {}
        }

        const allRawColumns = [...rawColumns, ...sourceColumns];
        const params = new URLSearchParams();

        allRawColumns.forEach((col: number) => {
          params.append("columns", col.toString());
        });

        params.append("range", "latest");
        params.append("tz", timezone);

        const res = await http.get(`/data/${config.device_id}/chart_data`, { params });
        const latestRaw = res.data?.[0];
        if (!latestRaw) return;

        const latestRows = normalizeChartRows([latestRaw], allRawColumns);
        const latest = latestRows[0];
        if (!latest) return;

        if (latest.timestamp === lastTimestampRef.current) return;

        lastTimestampRef.current = latest.timestamp;

        setChartData((prev: ChartPoint[]) => {
          const latestDate = parseAnyTimestamp(latest.timestamp);
          const oneDayAgo = latestDate.minus({ days: 1 });

          const filtered = prev.filter((p) => {
            const ts = parseAnyTimestamp(p.timestamp);
            return ts.isValid && ts >= oneDayAgo && p.timestamp !== latest.timestamp;
          });

          const rawOnly = removeStatColumnsFromRows([...filtered, latest]);

          if (statDefsForLatest.length > 0 && statChartColumns.length > 0) {
            const rangeEndTs = parseChartTimestamp(latest.timestamp);
            return injectStatRowsFor1D(rawOnly, statDefsForLatest, statChartColumns, rangeEndTs);
          }

          return rawOnly;
        });
      } catch {
        // ignore
      }
    };

    fetchLatest();
  }, [isChartType, selectedRange, config.device_id, derivedColumns, timezone, now]);

  useEffect(() => {
    const fetchLabels = async () => {
      try {
        const [rawRes, calcRes, statRes] = await Promise.all([
          http.get(`/device/${config.device_id}/labels`),
          http.get(`/calculated_params/${config.device_id}`),
          http.get(`/stat-definitions/device/${config.device_id}`),
        ]);

        const rawLabels = rawRes.data;
        const calcLabels = calcRes.data.map((c: any) => ({
          column_index: c.column_index,
          label: c.result_label,
        }));
        const statLabels = statRes.data.map((s: any) => ({
          column_index: 1000 + s.id,
          label: s.result_label,
        }));

        setDeviceLabels([...rawLabels, ...calcLabels, ...statLabels]);
      } catch {
        // ignore
      }
    };

    if (config.device_id && isChartType) {
      fetchLabels();
    }
  }, [config.device_id, isChartType]);

  // const handleRangeSelect = (range: string) => {
  //   setSelectedRange(range);
  //   if (range === "custom") setShowCustomModal(true);
  //   setRangeOpen(false);
  // };

  const handleRangeSelect = (range: string) => {
    handleResetZoom();
    setSelectedRange(range);

    if (range === "custom") {
      setShowCustomModal(true);
    }

    setRangeOpen(false);
  };

  const handleApplyCustom = () => {
    setShowCustomModal(false);
  };

  let contentComponent = null;
  if (config.type === "solid_gauge") {
    contentComponent = <SolidGaugeComponent config={config} />;
  } else if (config.type === "standard_chart") {
    contentComponent = (
      // <StandardChartComponent
      //   config={displayConfig}
      //   data={chartData}
      //   deviceLabels={deviceLabels}
      // />
      <StandardChartComponent
      config={displayConfig}
      data={zoomData ?? chartData}
      deviceLabels={deviceLabels}
      onZoomRange={handleZoomRange}
      onResetZoom={handleResetZoom}
      isZoomed={zoomData !== null}
    />
    );
  } else if (config.type === "windrose_chart") {
    contentComponent = (
      <WindroseChartCard
        config={displayConfig}
        data={chartData}
        rangeLabel={(() => {
          if (selectedRange === "custom" && customRange[0] && customRange[1]) {
            const fmt = (d: Date) => DateTime.fromJSDate(d).toFormat("yyyy-MM-dd HH:mm:ss");
            return `${fmt(customRange[0])} → ${fmt(customRange[1])}`;
          }
          if (chartData.length > 1) {
            return `${chartData[0].timestamp} → ${chartData[chartData.length - 1]!.timestamp}`;
          }
          if (chartData.length === 1) {
            return chartData[0].timestamp;
          }
          return "No Data";
        })()}
      />
    );
  } else if (config.type === "boolean_indicator") {
    contentComponent = (
    <BooleanIndicatorComponent config={config} />
    );
  } else if (config.type === "temperature_gauge") {
    contentComponent = (
    <TemperatureGaugeComponent config={config} />
    );
  } else {
    contentComponent = <p>Unknown graph type</p>;
  }

  return (
    <>
      <div
        className="position-relative d-flex flex-column"
        style={{
          backgroundColor: "#001f3f",
          borderRadius: "2px",
          color: "#ffffff",
          width: "100%",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <div
          className="d-flex align-items-top p-0 drag-handle"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{ backgroundColor: "#002f5f", fontSize: "0.8rem", fontWeight: "600" }}
        >
          <div className="flex-grow-1 text-center">
            <strong>
              {config.type === "solid_gauge" ||
                config.type === "boolean_indicator" ||
                config.type === "temperature_gauge"  ? (
                  <>
                    {config.device_label?.label ||
                      config.title ||
                      "Label"}
                    {config.unit && (
                      <span
                        style={{ fontSize: "0.8em", color: "#ccc" }}
                      >
                        {" "}{config.unit}
                      </span>
                    )}
                  </>
                ) : (
                  config.title || "Graph"
                )}
            </strong>
          </div>

          <div className="d-flex gap-2 align-items-center">
            {isChartType && (hovered || rangeOpen) && (
              <>
                <Button
                  variant="light"
                  size="sm"
                  className="no-drag"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setShowFullscreen(true);
                  }}
                >
                  <Maximize2 size={16} />
                </Button>

                <Dropdown show={rangeOpen} onToggle={(isOpen) => setRangeOpen(!!isOpen)}>
                  <Dropdown.Toggle
                    variant="light"
                    size="sm"
                    className="no-drag"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    📅
                  </Dropdown.Toggle>

                  <Dropdown.Menu onMouseDown={(e) => e.stopPropagation()}>
                    {["1d", "1w", "1m", "3m", "custom"].map((range) => (
                      <Dropdown.Item
                        key={range}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRangeSelect(range);
                          setRangeOpen(false);
                        }}
                      >
                        {range.toUpperCase()}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </>
            )}

            {(role === "admin" ||
              role === "owner" ||
              (role === "client" && clientRole === "manager")) &&
              (hovered || dropdownOpen) && (
                <Dropdown show={dropdownOpen} onToggle={(isOpen) => setDropdownOpen(!!isOpen)}>
                  <Dropdown.Toggle
                    variant="light"
                    size="sm"
                    className="no-drag"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    ⋮
                  </Dropdown.Toggle>

                  <Dropdown.Menu onMouseDown={(e) => e.stopPropagation()}>
                    <Dropdown.Item
                      onClick={(e) => {
                        e.stopPropagation();
                        onDuplicate(config);
                        setDropdownOpen(false);
                      }}
                    >
                      📄 Duplicate
                    </Dropdown.Item>

                    <Dropdown.Item
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowProperties(true);
                        setDropdownOpen(false);
                      }}
                    >
                      ⚙ Properties
                    </Dropdown.Item>

                    <Dropdown.Divider />

                    <Dropdown.Item
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                        setDropdownOpen(false);
                      }}
                      className="text-danger"
                    >
                      🗑 Delete
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
          </div>
        </div>

        <div className="flex-grow-1 d-flex justify-content-center align-items-center p-0">
          {contentComponent}
        </div>
      </div>

      {showFullscreen && (
        <Modal show fullscreen onHide={() => setShowFullscreen(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{config.title || "Full Screen View"}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-dark text-light">
            {config.type === "standard_chart" && (
              // <StandardChartComponent
              //   config={displayConfig}
              //   data={chartData}
              //   deviceLabels={deviceLabels}
              // />
              <StandardChartComponent
              config={displayConfig}
              data={zoomData ?? chartData}
              deviceLabels={deviceLabels}
              onZoomRange={handleZoomRange}
              onResetZoom={handleResetZoom}
              isZoomed={zoomData !== null}
            />
            )}
            {config.type === "windrose_chart" && (
              <WindroseChartCard
                config={displayConfig}
                data={chartData}
                rangeLabel={(() => {
                  if (selectedRange === "custom" && customRange[0] && customRange[1]) {
                    const fmt = (d: Date) => DateTime.fromJSDate(d).toFormat("yyyy-MM-dd HH:mm:ss");
                    return `${fmt(customRange[0])} → ${fmt(customRange[1])}`;
                  }
                  if (chartData.length > 1) {
                    return `${chartData[0].timestamp} → ${chartData[chartData.length - 1]!.timestamp}`;
                  }
                  if (chartData.length === 1) {
                    return chartData[0].timestamp;
                  }
                  return selectedRange.toUpperCase();
                })()}
              />
            )}
          </Modal.Body>
        </Modal>
      )}

      {showCustomModal && (
        <Modal show onHide={() => setShowCustomModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Select Custom Date Range</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col>
                <label className="mb-1">From:</label>
                <DatePicker
                  selected={customRange[0] ?? undefined}
                  onChange={(date: Date | null) => setCustomRange([date, customRange[1]])}
                  selectsStart
                  startDate={customRange[0] ?? undefined}
                  endDate={customRange[1] ?? undefined}
                  showTimeSelect
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  className="form-control"
                />
              </Col>
              <Col>
                <label className="mb-1">To:</label>
                <DatePicker
                  selected={customRange[1] ?? undefined}
                  onChange={(date: Date | null) => setCustomRange([customRange[0], date])}
                  selectsEnd
                  startDate={customRange[0] ?? undefined}
                  endDate={customRange[1] ?? undefined}
                  minDate={customRange[0] ?? undefined}
                  showTimeSelect
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  className="form-control"
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCustomModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleApplyCustom}
              disabled={!customRange[0] || !customRange[1]}
            >
              Apply
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {showProperties && (
        <>
          {config.type === "solid_gauge" && (
            <GaugePropertiesModal
              show={true}
              onClose={() => setShowProperties(false)}
              config={config}
              deviceId={config.device_id}
              refetchGraphs={() => window.location.reload()}
            />
          )}
          {config.type === "standard_chart" && (
            <ChartPropertiesModal
              show={true}
              onClose={() => setShowProperties(false)}
              config={config}
              deviceId={config.device_id}
              refetchGraphs={() => window.location.reload()}
            />
          )}
          {config.type === "windrose_chart" && (
            <WindrosePropertiesModal
              show={true}
              onClose={() => setShowProperties(false)}
              config={config}
              deviceId={config.device_id}
              refetchGraphs={() => window.location.reload()}
            />
          )}
          {config.type === "boolean_indicator" && (
            <BooleanIndicatorProperties
              show={true}
              onClose={() => setShowProperties(false)}
              config={config}
              deviceId={config.device_id}
              refetchGraphs={() => window.location.reload()}
            />
          )}
          {config.type === "temperature_gauge" && (
            <TemperatureGaugeProperties
              show={true}
              onClose={() => setShowProperties(false)}
              config={config}
              deviceId={config.device_id}
              refetchGraphs={() => window.location.reload()}
            />
          )}
        </>
      )}
    </>
  );
}

export default DashboardCard;